import type { PointCloudFrame } from "../../DataServer/messaging";

/**
 * The bytes of decoded frames a point cloud keeps on the main thread. A point
 * costs 12 bytes of position and 4 of gradient lookups, plus 4 bytes of color
 * when the frame has colors, so a frame of the `voxel_map/viz` topic this was
 * sized on (~45k points, no colors) is ~0.72 MB and 256 MiB holds about 370
 * of them: nearly 2 min of a 3.3 Hz topic to replay or scrub through without
 * a worker round trip. At the other end, a colored 1M-point frame is 20 MB,
 * so the budget holds 13 of those. The frame on screen is charged too, since
 * it is held here.
 */
export const POINT_CLOUD_CACHE_BYTES = 256 * 1024 * 1024;

/** The bytes a frame's vertex buffers take: what the cache charges for it. */
export function frameBytes(frame: PointCloudFrame): number {
  return (
    frame.positions.byteLength +
    frame.measures.byteLength +
    (frame.colors?.byteLength ?? 0)
  );
}

/** Half the budget: the frames of one stretch of playback, in no order. */
interface Bucket {
  frames: Map<bigint, PointCloudFrame>;
  bytes: number;
}

/**
 * Decoded frames by log time, in two buckets of half the budget each, so
 * playback can be read ahead of without end.
 *
 * One bucket is *current*: it holds the frame on screen and fills with the
 * frames after it as they are fetched ahead. Once it is full, the frames
 * after it go to the *next* bucket. When playback reaches the next bucket,
 * the current one has been played through: it is dropped whole, and becomes
 * the next bucket, taking the frames after the one now current. The two
 * trade places like that for as long as playback runs, so a frame fetched
 * ahead is never evicted by another fetched ahead — the one way a cache that
 * evicted frame by frame could thrash — and the frames behind the playhead
 * are let go a bucket at a time rather than kept until the budget forced it.
 *
 * Within a bucket there is no order of use. The frame on screen is never
 * evicted: the mesh holds the same arrays, so evicting it would free nothing,
 * and it would cost a fetch to show again. A frame fetched because it is
 * wanted always lands: into the current bucket when it falls within or before
 * the frames there, a scrub back, evicting the frames farthest from it if
 * there is no room; else into the next bucket, evicting its earliest frames
 * likewise. Showing it then drops the played bucket as playback would have.
 * A frame larger than a bucket is held over budget, as it was fetched to be
 * shown.
 */
export class PointCloudFrameCache {
  private readonly buckets: [Bucket, Bucket] = [emptyBucket(), emptyBucket()];
  /** Index of the current bucket; the other is next. */
  private current = 0;
  /** The log time of the frame on screen, which is never evicted. */
  private shownLogTime: bigint | undefined;
  /** The largest frame put since the last clear: the size the next one is assumed to have. */
  private largestFrameBytes = 0;
  private readonly bucketBytes: number;

  constructor(maxBytes: number = POINT_CLOUD_CACHE_BYTES) {
    this.bucketBytes = maxBytes / 2;
  }

  get size(): number {
    return this.buckets[0].frames.size + this.buckets[1].frames.size;
  }

  get byteLength(): number {
    return this.buckets[0].bytes + this.buckets[1].bytes;
  }

  has(logTime: bigint): boolean {
    return this.bucketOf(logTime) !== undefined;
  }

  /** The frame at `logTime`, or undefined. */
  get(logTime: bigint): PointCloudFrame | undefined {
    return this.bucketOf(logTime)?.frames.get(logTime);
  }

  /**
   * Whether a frame as large as the largest put so far fits in either bucket
   * without evicting anything. What fetching ahead checks first: a frame
   * fetched ahead into full buckets would only evict one fetched ahead
   * before it, which the next poll would then fetch again.
   */
  canGrow(): boolean {
    return this.buckets.some((bucket) =>
      this.fits(bucket, this.largestFrameBytes),
    );
  }

  /**
   * Adds `frame`, replacing any frame at its log time: into the current
   * bucket when it falls within or before the frames there, or after them
   * while there is room; else into the next bucket. A bucket with no room
   * for it drops the frames farthest from it, never the one on screen.
   */
  put(frame: PointCloudFrame): void {
    const bytes = frameBytes(frame);
    this.largestFrameBytes = Math.max(this.largestFrameBytes, bytes);
    const holder = this.bucketOf(frame.logTime);
    if (holder !== undefined) {
      holder.bytes += bytes - frameBytes(holder.frames.get(frame.logTime)!);
      holder.frames.set(frame.logTime, frame);
      return;
    }
    const current = this.buckets[this.current];
    const next = this.buckets[1 - this.current];
    const last = latestLogTime(current);
    const target =
      last !== undefined && frame.logTime > last && !this.fits(current, bytes)
        ? next
        : current;
    this.makeRoom(target, frame.logTime, bytes);
    target.frames.set(frame.logTime, frame);
    target.bytes += bytes;
  }

  /**
   * Records that the frame at `logTime` is on screen. When that frame is in
   * the next bucket, playback has played the current one through: it is
   * dropped, and the next bucket is current from now on.
   */
  shown(logTime: bigint): void {
    this.shownLogTime = logTime;
    const next = this.buckets[1 - this.current];
    if (next.frames.has(logTime)) {
      const played = this.buckets[this.current];
      played.frames.clear();
      played.bytes = 0;
      this.current = 1 - this.current;
    }
  }

  clear(): void {
    for (const bucket of this.buckets) {
      bucket.frames.clear();
      bucket.bytes = 0;
    }
    this.shownLogTime = undefined;
    this.largestFrameBytes = 0;
  }

  private bucketOf(logTime: bigint): Bucket | undefined {
    return this.buckets.find((bucket) => bucket.frames.has(logTime));
  }

  private fits(bucket: Bucket, bytes: number): boolean {
    return bucket.bytes + bytes <= this.bucketBytes;
  }

  /**
   * Evicts from `bucket`, farthest from `logTime` first and never the frame
   * on screen, until `bytes` fit or nothing evictable is left.
   */
  private makeRoom(bucket: Bucket, logTime: bigint, bytes: number): void {
    if (this.fits(bucket, bytes)) {
      return;
    }
    const evictable = [...bucket.frames.keys()]
      .filter((held) => held !== this.shownLogTime)
      .sort((a, b) =>
        compareBigInt(distance(b, logTime), distance(a, logTime)),
      );
    for (const held of evictable) {
      if (this.fits(bucket, bytes)) {
        return;
      }
      bucket.bytes -= frameBytes(bucket.frames.get(held)!);
      bucket.frames.delete(held);
    }
  }
}

function emptyBucket(): Bucket {
  return { frames: new Map(), bytes: 0 };
}

/** The latest log time a bucket holds, or undefined when it holds nothing. */
function latestLogTime(bucket: Bucket): bigint | undefined {
  let latest: bigint | undefined;
  for (const logTime of bucket.frames.keys()) {
    if (latest === undefined || logTime > latest) {
      latest = logTime;
    }
  }
  return latest;
}

function distance(a: bigint, b: bigint): bigint {
  return a > b ? a - b : b - a;
}

function compareBigInt(a: bigint, b: bigint): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}
